<template>
    <div class="bilibili-login-container">
        <!-- ==================== Open API OAuth 授权 ==================== -->
        <el-card class="oauth-card">
            <template #header>
                <div class="header-title">
                    <el-icon class="title-icon"><Link /></el-icon>
                    <span>B站开放平台 OAuth 授权</span>
                    <el-tag v-if="openApiStatus.authorized" type="success" size="small" class="status-tag">已授权</el-tag>
                    <el-tag v-else-if="openApiStatus.loading" type="warning" size="small" class="status-tag">检查中...</el-tag>
                    <el-tag v-else type="danger" size="small" class="status-tag">未授权</el-tag>
                </div>
            </template>

            <!-- 已授权状态 -->
            <div class="oauth-authorized" v-if="openApiStatus.authorized">
                <el-alert title="Open API 授权正常" type="success" :closable="false" show-icon>
                    <p>Works页面将通过Open API获取视频和专栏数据。</p>
                </el-alert>
                <p class="expire-info" v-if="openApiStatus.expireTime">
                    Token有效期至：{{ formatTime(openApiStatus.expireTime) }}
                </p>
                <div class="oauth-actions-row">
                    <el-button type="warning" @click="refreshToken" :loading="refreshLoading" size="small">
                        <el-icon><Refresh /></el-icon> 刷新Token
                    </el-button>
                    <el-button type="danger" @click="revokeOAuth" :loading="revokeLoading" size="small" plain>
                        <el-icon><SwitchButton /></el-icon> 退出授权
                    </el-button>
                </div>
            </div>

            <!-- 未授权状态 -->
            <div class="oauth-unauthorized" v-else>
                <el-steps :active="oauthStep" align-center class="oauth-steps">
                    <el-step title="获取授权链接" />
                    <el-step title="前往B站授权" />
                    <el-step title="检查授权状态" />
                </el-steps>

                <!-- 步骤1：获取授权链接 -->
                <div class="oauth-step-content">
                    <el-button type="primary" @click="getOAuthUrl" :loading="authUrlLoading" size="large">
                        <el-icon><Link /></el-icon> 获取B站授权链接
                    </el-button>

                    <div class="oauth-url-box" v-if="oauthUrl">
                        <el-alert title="请在新窗口打开以下链接完成授权" type="warning" :closable="false" show-icon />
                        <div class="url-display">
                            <el-input v-model="oauthUrl" readonly>
                                <template #append>
                                    <el-button @click="openOAuthUrl">打开链接</el-button>
                                    <el-button @click="copyOAuthUrl">复制</el-button>
                                </template>
                            </el-input>
                        </div>
                        <p class="oauth-tip">完成授权后B站会自动回调，回到本页面点击下方按钮检查状态。</p>
                        <div class="oauth-actions">
                            <el-button type="success" @click="checkOpenApiStatus" :loading="statusLoading">
                                <el-icon><CircleCheck /></el-icon> 检查授权状态
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- 手动设置Token（备选） -->
                <el-divider />
                <el-collapse>
                    <el-collapse-item title="手动设置Token（高级）" name="manual">
                        <div class="manual-token">
                            <el-form label-width="120px">
                                <el-form-item label="Access Token">
                                    <el-input v-model="manualToken.accessToken" placeholder="粘贴 access_token" />
                                </el-form-item>
                                <el-form-item label="Refresh Token">
                                    <el-input v-model="manualToken.refreshToken" placeholder="粘贴 refresh_token（可选）" />
                                </el-form-item>
                                <el-form-item label="有效期(秒)">
                                    <el-input-number v-model="manualToken.expiresIn" :min="1" placeholder="默认30天" />
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="setManualToken" :loading="manualLoading">保存Token</el-button>
                                </el-form-item>
                            </el-form>
                        </div>
                    </el-collapse-item>
                </el-collapse>
            </div>
        </el-card>


        <!-- ==================== 缓存管理 ==================== -->
        <el-card class="cache-card">
            <template #header>
                <div class="header-title">
                    <el-icon class="title-icon"><DataBoard /></el-icon>
                    <span>缓存管理</span>
                </div>
            </template>
            <div class="cache-info" v-if="cacheStatus">
                <div class="cache-item">
                    <div class="cache-label">内存缓存：</div>
                    <div class="cache-value">
                        <el-tag :type="cacheStatus.memoryCache.exists ? 'success' : 'info'" size="small">
                            {{ cacheStatus.memoryCache.exists ? '已缓存' : '无缓存' }}
                        </el-tag>
                        <span v-if="cacheStatus.memoryCache.exists" class="cache-time">
                            (剩余{{ cacheStatus.memoryCache.remainingTime }}秒)
                        </span>
                    </div>
                </div>
                <div class="cache-item">
                    <div class="cache-label">数据库缓存：</div>
                    <div class="cache-value">
                        <el-tag :type="cacheStatus.databaseCache.exists ? 'success' : 'info'" size="small">
                            {{ cacheStatus.databaseCache.exists ? '已缓存' : '无缓存' }}
                        </el-tag>
                        <span v-if="cacheStatus.databaseCache.exists" class="cache-time">
                            ({{ Math.round(cacheStatus.databaseCache.age / 60) }}分钟前)
                        </span>
                    </div>
                </div>
            </div>
            <div class="cache-actions">
                <el-button @click="getCacheStatus" :loading="cacheLoading" size="small">
                    <el-icon><Refresh /></el-icon> 刷新状态
                </el-button>
                <el-button type="warning" @click="clearCache" :loading="clearLoading" size="small">
                    <el-icon><Delete /></el-icon> 清理缓存
                </el-button>
            </div>
        </el-card>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
    DataBoard, Refresh, Delete,
    Link, CircleCheck, SwitchButton
} from '@element-plus/icons-vue';
import axios from 'axios';

// ==================== Open API OAuth 状态 ====================
const openApiStatus = reactive({
    authorized: false,
    loading: true,
    expireTime: null
});
const oauthUrl = ref('');
const oauthStep = ref(0);
const authUrlLoading = ref(false);
const statusLoading = ref(false);
const refreshLoading = ref(false);
const revokeLoading = ref(false);
const manualLoading = ref(false);
const manualToken = reactive({ accessToken: '', refreshToken: '', expiresIn: 2592000 });

// ==================== 缓存管理状态 ====================
const cacheStatus = ref(null);
const cacheLoading = ref(false);
const clearLoading = ref(false);

// 扫码登录轮询定时器（原代码未声明，直接赋值在严格模式下会抛 ReferenceError）
let pollTimer = null;

onMounted(() => {
    checkOpenApiStatus();
    getCacheStatus();
});

// 组件卸载时清理轮询，避免定时器泄漏
onUnmounted(() => {
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
});

// ==================== Open API OAuth 方法 ====================

// 检查 Open API 授权状态
const checkOpenApiStatus = async () => {
    statusLoading.value = true;
    openApiStatus.loading = true;
    try {
        const res = await axios.get('/adminapi/bilibili/openapi/status');
        if (res.data.code === 0) {
            openApiStatus.authorized = res.data.data.authorized;
            openApiStatus.expireTime = res.data.data.expireTime;
            if (res.data.data.authorized) {
                oauthStep.value = 3;
            }
        }
    } catch (e) {
        console.error('检查OpenAPI状态失败:', e);
    } finally {
        openApiStatus.loading = false;
        statusLoading.value = false;
    }
};

// 步骤1：获取OAuth授权URL并直接跳转
const getOAuthUrl = async () => {
    authUrlLoading.value = true;
    try {
        const res = await axios.get('/adminapi/bilibili/openapi/authurl');
        if (res.data.code === 0) {
            oauthUrl.value = res.data.data.authUrl;
            oauthStep.value = 1;
            ElMessage.success('即将跳转到B站授权页面');
            // 直接跳转到B站授权页（同窗口）
            window.location.href = oauthUrl.value;
        } else {
            ElMessage.error(res.data.message || '获取授权链接失败');
        }
    } catch (e) {
        ElMessage.error('网络错误');
    } finally {
        authUrlLoading.value = false;
    }
};

// 打开授权链接
const openOAuthUrl = () => {
    if (oauthUrl.value) {
        window.open(oauthUrl.value, '_blank');
        oauthStep.value = 2;
        ElMessage.info('请在新窗口中完成B站授权，完成后回到本页面检查状态');
    }
};

// 复制授权链接
const copyOAuthUrl = () => {
    if (oauthUrl.value) {
        navigator.clipboard.writeText(oauthUrl.value);
        ElMessage.success('已复制授权链接');
    }
};

// 刷新Token
const refreshToken = async () => {
    refreshLoading.value = true;
    try {
        const res = await axios.post('/adminapi/bilibili/openapi/refresh');
        if (res.data.code === 0) {
            ElMessage.success('Token刷新成功');
            await checkOpenApiStatus();
        } else {
            ElMessage.error(res.data.message || '刷新失败');
        }
    } catch (e) {
        ElMessage.error('网络错误');
    } finally {
        refreshLoading.value = false;
    }
};

// 退出 OAuth 授权
const revokeOAuth = async () => {
    try {
        await ElMessageBox.confirm(
            '退出后Works页面将无法通过Open API获取数据，确定要退出B站开放平台授权吗？',
            '确认退出',
            { confirmButtonText: '确定退出', cancelButtonText: '取消', type: 'warning' }
        );
        revokeLoading.value = true;
        const res = await axios.post('/adminapi/bilibili/openapi/revoke');
        if (res.data.code === 0) {
            ElMessage.success('已退出B站开放平台授权');
            openApiStatus.authorized = false;
            openApiStatus.expireTime = null;
            oauthStep.value = 0;
        } else {
            ElMessage.error(res.data.message || '退出失败');
        }
    } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
            ElMessage.error('网络错误');
        }
    } finally {
        revokeLoading.value = false;
    }
};

// 手动设置Token
const setManualToken = async () => {
    if (!manualToken.accessToken) {
        ElMessage.warning('请输入Access Token');
        return;
    }
    manualLoading.value = true;
    try {
        const res = await axios.post('/adminapi/bilibili/openapi/settoken', {
            access_token: manualToken.accessToken,
            refresh_token: manualToken.refreshToken,
            expires_in: manualToken.expiresIn
        });
        if (res.data.code === 0) {
            ElMessage.success('Token设置成功');
            await checkOpenApiStatus();
        } else {
            ElMessage.error(res.data.message || '保存失败');
        }
    } catch (e) {
        ElMessage.error('网络错误');
    } finally {
        manualLoading.value = false;
    }
};

// 格式化Unix时间戳
const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    return d.toLocaleString('zh-CN');
};

// 检查登录状态
const checkLoginStatus = async () => {
    try {
        const response = await axios.get('/adminapi/bilibili/status');
        if (response.data.code === 0) {
            loginStatus.isLoggedIn = response.data.data.isLoggedIn;
            loginStatus.userInfo = response.data.data.userInfo;
        }
    } catch (error) {
        console.error('检查登录状态失败:', error);
    }
};

// 生成登录二维码
const generateQRCode = async () => {
    qrLoading.value = true;
    qrExpired.value = false;
    statusText.value = '生成中...';
    statusClass.value = 'loading';

    try {
        // 停止可能存在的旧轮询
        if (pollTimer) {
            clearInterval(pollTimer);
        }

        const response = await axios.get('/adminapi/bilibili/login');
        console.log('二维码API响应:', response.data);
        
        if (response.data.code === 0) {
            const data = response.data.data;
            qrCodeKey.value = data.qrcode_key;
            
            // 使用QRCode库生成二维码图片，参考userlogin.vue的做法
            const loginUrl = data.url;
            
            QRCode.toDataURL(loginUrl, { width: 200 }, (err, url) => {
                if (err) {
                    console.error('生成二维码图片失败:', err);
                    ElMessage.error('生成二维码图片失败');
                    qrLoading.value = false;
                    return;
                }
                
                qrCodeUrl.value = url;
                statusText.value = '等待扫码...';
                statusClass.value = 'waiting';
                qrLoading.value = false;
                
                console.log('二维码生成成功');
                
                // 开始轮询检查登录状态
                startPolling();
                
                ElMessage.success('二维码生成成功，请使用B站APP扫码登录');
            });
        } else {
            ElMessage.error(response.data.message || '生成二维码失败');
            qrLoading.value = false;
        }
    } catch (error) {
        console.error('生成二维码失败:', error);
        ElMessage.error('网络错误，请重试');
        qrLoading.value = false;
    }
};

// 开始轮询检查登录状态
const startPolling = () => {
    if (pollTimer) {
        clearInterval(pollTimer);
    }

    pollTimer = setInterval(async () => {
        try {
            const response = await axios.post('/adminapi/bilibili/login/check', {
                qrcode_key: qrCodeKey.value
            });

            const data = response.data;
            
            if (data.code === 0) {
                // 登录成功
                statusText.value = '登录成功！';
                statusClass.value = 'success';
                clearInterval(pollTimer);
                
                ElMessage.success('B站登录成功！');
                
                // 延迟刷新状态
                setTimeout(() => {
                    checkLoginStatus();
                    qrCodeUrl.value = '';
                    qrCodeKey.value = '';
                }, 1500);
                
            } else if (data.code === 1) {
                // 未扫码
                statusText.value = '等待扫码...';
                statusClass.value = 'waiting';
            } else if (data.code === 2) {
                // 已扫码未确认
                statusText.value = '已扫码，请在手机上确认登录';
                statusClass.value = 'scanned';
            } else if (data.code === -2) {
                // 二维码过期
                statusText.value = '二维码已过期';
                statusClass.value = 'expired';
                qrExpired.value = true;
                clearInterval(pollTimer);
            } else {
                // 其他错误
                statusText.value = data.message || '登录检查失败';
                statusClass.value = 'error';
                clearInterval(pollTimer);
            }
        } catch (error) {
            console.error('检查登录状态失败:', error);
            statusText.value = '网络错误';
            statusClass.value = 'error';
        }
    }, 2000); // 每2秒检查一次
};

// 退出登录
const logout = async () => {
    logoutLoading.value = true;
    
    try {
        const response = await axios.post('/adminapi/bilibili/logout');
        if (response.data.code === 0) {
            loginStatus.isLoggedIn = false;
            loginStatus.userInfo = null;
            ElMessage.success('已退出B站登录');
        } else {
            ElMessage.error(response.data.message || '退出登录失败');
        }
    } catch (error) {
        console.error('退出登录失败:', error);
        ElMessage.error('网络错误，请重试');
    } finally {
        logoutLoading.value = false;
    }
};

// 获取缓存状态
const getCacheStatus = async () => {
    cacheLoading.value = true;
    
    try {
        const response = await axios.get('/adminapi/cache/status');
        if (response.data.code === 0) {
            cacheStatus.value = response.data.data;
        } else {
            ElMessage.error(response.data.message || '获取缓存状态失败');
        }
    } catch (error) {
        console.error('获取缓存状态失败:', error);
        ElMessage.error('网络错误，请重试');
    } finally {
        cacheLoading.value = false;
    }
};

// 清理缓存
const clearCache = async () => {
    clearLoading.value = true;
    
    try {
        const response = await axios.post('/adminapi/cache/clear');
        if (response.data.code === 0) {
            ElMessage.success('缓存清理成功');
            // 刷新缓存状态
            await getCacheStatus();
        } else {
            ElMessage.error(response.data.message || '清理缓存失败');
        }
    } catch (error) {
        console.error('清理缓存失败:', error);
        ElMessage.error('网络错误，请重试');
    } finally {
        clearLoading.value = false;
    }
};
</script>

<style scoped lang="scss">
.bilibili-login-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.header-title {
    display: flex;
    align-items: center;
    font-size: 1.1rem;
    font-weight: 600;
    .title-icon { margin-right: 8px; color: #409eff; }
    .status-tag { margin-left: 12px; }
}

// ==================== OAuth 卡片 ====================
.oauth-card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.oauth-authorized {
    .expire-info {
        color: #909399;
        font-size: 13px;
        margin: 8px 0 12px;
    }
    .oauth-actions-row {
        display: flex;
        gap: 12px;
        align-items: center;
    }
}

.oauth-unauthorized {
    .oauth-steps { margin-bottom: 24px; }
}

.oauth-step-content {
    text-align: center;
    padding: 10px 0;

    .oauth-url-box {
        margin-top: 20px;
        text-align: left;
    }

    .url-display { margin-top: 12px; }

    .oauth-tip {
        color: #909399;
        font-size: 13px;
        margin: 12px 0 0;
    }

    .oauth-actions {
        margin-top: 16px;
        text-align: center;
    }
}

.manual-token {
    padding: 10px 20px;
}

// ==================== 登录卡片 ====================
.login-card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.login-status {
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 8px;
    .status-info {
        display: flex;
        align-items: center;
        .user-avatar { margin-right: 16px; }
        .user-info {
            h3 { margin: 0 0 4px; color: #303133; }
            p { margin: 0 0 8px; color: #606266; font-size: 14px; }
        }
    }
}

.login-area { padding: 20px; }

.login-description {
    margin-bottom: 30px;
    .el-alert p { margin: 4px 0; line-height: 1.5; }
}

.qr-login-area {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.qr-container {
    text-align: center;
    width: 100%;
    max-width: 400px;
    .qr-title {
        margin-bottom: 20px;
        h4 { margin: 0 0 8px; color: #303133; }
        .qr-status {
            margin: 0; font-size: 14px;
            &.waiting { color: #909399; }
            &.loading { color: #409eff; }
            &.scanned { color: #e6a23c; }
            &.success { color: #67c23a; }
            &.expired, &.error { color: #f56c6c; }
        }
    }
    .qr-code-wrapper {
        position: relative;
        display: inline-block;
        margin-bottom: 20px;
        .qr-code {
            width: 200px; height: 200px;
            border: 1px solid #dcdfe6;
            border-radius: 8px;
            background: #fff;
            cursor: pointer;
        }
        .qr-expired-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            .expired-icon { font-size: 40px; margin-bottom: 8px; color: #f56c6c; }
            p { margin: 0 0 16px; font-size: 16px; }
        }
    }
}

.generate-qr {
    text-align: center;
    padding: 40px 20px;
    .el-button .el-icon { margin-right: 8px; }
}

.qr-actions { display: flex; justify-content: center; }

// ==================== 缓存卡片 ====================
.cache-card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.cache-info { margin-bottom: 0; }

.cache-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
    &:last-child { border-bottom: none; }
    .cache-label { font-weight: 500; color: #303133; }
    .cache-value {
        display: flex;
        align-items: center;
        gap: 8px;
        .cache-time { font-size: 12px; color: #909399; }
    }
}

.cache-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}
</style>